import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { post } from '../lib/api';

const KNOWN_ADDONS = ['bot', 'owner_panel', 'fivem_panel', 'wavelink', 'audit', 'music', 'BackRoom', 'economy', 'security'];

export function BackroomLogsPage() {
  const [lines,      setLines]      = useState<string[]>([]);
  const offsetRef   = useRef(0);
  const [paused,     setPaused]     = useState(false);
  const [filter,     setFilter]     = useState<string | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const bottomRef   = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchLogs = async () => {
    try {
      const data = await post<{ lines: string[]; next_offset: number }>(
        '/panel/backroom/logs',
        { offset: offsetRef.current }
      );
      if (data.lines && data.lines.length > 0) {
        offsetRef.current = data.next_offset;
        setLines(prev => [...prev, ...data.lines].slice(-500));
      }
    } catch (err: unknown) {
      toast.error('Log fetch error: ' + (err instanceof Error ? err.message : 'Unknown'));
    }
  };

  useEffect(() => { fetchLogs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (paused) { if (intervalRef.current) clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(fetchLogs, 3000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [paused]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lines, autoScroll]);

  const filteredLines = filter
    ? lines.filter(l => l.toLowerCase().includes(filter.toLowerCase()))
    : lines;

  const colorize = (line: string) => {
    if (line.includes('[ERROR]') || line.includes('ERROR'))     return '#f87171';
    if (line.includes('[WARNING]') || line.includes('WARNING')) return '#fbbf24';
    if (line.includes('[INFO]'))    return '#cbd5e1';
    if (line.includes('[DEBUG]'))   return '#64748b';
    return '#94a3b8';
  };

  return (
    <div className="flex flex-col gap-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[9px] font-mono tracking-[0.2em] text-slate-600 uppercase mb-1">BackRoom</div>
          <h1 className="text-2xl font-black uppercase tracking-tight text-white">
            The <span className="text-cyberGreen neon-text-glow">Backrooms</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Live tail of <code className="font-mono text-slate-400">logs/bot.log</code> — polls every 3s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPaused(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border
              ${paused
                ? 'bg-cyberGreen/10 border-cyberGreen/20 text-cyberGreen hover:bg-cyberGreen/15'
                : 'bg-white/[0.03] border-white/[0.08] text-slate-400 hover:text-white hover:bg-white/[0.06]'
              }`}
          >
            <i className={`fa-solid ${paused ? 'fa-play' : 'fa-pause'} text-xs`} />
            {paused ? 'Resume' : 'Pause'}
          </button>
          <button
            onClick={() => { setLines([]); offsetRef.current = 0; }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider bg-white/[0.03] border border-white/[0.08] text-slate-400 hover:text-white hover:bg-white/[0.06] transition-all"
          >
            <i className="fa-solid fa-eraser text-xs" />
            Clear
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[9px] font-mono text-slate-600 uppercase tracking-wider">Filter:</span>
        <button
          onClick={() => setFilter(null)}
          className={`px-2.5 py-1 rounded-lg text-[9px] font-mono font-bold tracking-wider uppercase transition-all border
            ${filter === null
              ? 'bg-cyberGreen/10 border-cyberGreen/20 text-cyberGreen'
              : 'bg-white/[0.02] border-white/[0.06] text-slate-500 hover:text-slate-300 hover:border-white/[0.1]'
            }`}
        >
          All
        </button>
        {KNOWN_ADDONS.map(addon => (
          <button
            key={addon}
            onClick={() => setFilter(filter === addon ? null : addon)}
            className={`px-2.5 py-1 rounded-lg text-[9px] font-mono font-bold tracking-wider uppercase transition-all border
              ${filter === addon
                ? 'bg-cyberGreen/10 border-cyberGreen/20 text-cyberGreen'
                : 'bg-white/[0.02] border-white/[0.06] text-slate-500 hover:text-slate-300 hover:border-white/[0.1]'
              }`}
          >
            {addon}
          </button>
        ))}
      </div>

      {/* Terminal */}
      <div className="glass-card rounded-2xl overflow-hidden">
        {/* Status bar */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.05]">
          <div className="flex items-center gap-2 text-[9px] font-mono text-slate-500">
            <span className={`w-1.5 h-1.5 rounded-full ${paused ? 'bg-amber-400' : 'bg-cyberGreen shadow-[0_0_4px_#00ff66] animate-pulse'}`} />
            {paused ? 'Paused' : 'Live'}
            <span className="ml-2">{filteredLines.length} lines</span>
            {filter && <span className="text-cyberGreen">· filter: {filter}</span>}
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer text-[9px] font-mono text-slate-500 select-none">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={e => setAutoScroll(e.target.checked)}
              className="w-3 h-3 accent-[#00ff66]"
            />
            Auto-scroll
          </label>
        </div>

        {/* Log output */}
        <div className="h-[480px] overflow-y-auto p-4 font-mono text-xs leading-relaxed scrollbar-thin bg-black/40">
          {filteredLines.length === 0 ? (
            <span className="text-slate-600 italic">No log lines yet — waiting for data...</span>
          ) : filteredLines.map((line, i) => (
            <div key={i} style={{ color: colorize(line) }}>{line}</div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
}
